import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    isDark: boolean;
    colors: {
        primaryRed: string;
        softPink: string;
        background: string;
        accentYellow: string;
        card: string;
        text: string;
        textSecondary: string;
        border: string;
    };
}

const lightColors = {
    primaryRed: '#E63946',
    softPink: '#FFB3B8',
    background: '#FFF1F2',
    card: '#FFFFFF',
    text: '#2B2B2B',
    textSecondary: '#666666',
    border: "#FFB3B8",
    accentYellow: '#FFD700',
};

const darkColors = {
    primaryRed: '#E63946',
    softPink: '#FFB3B8',
    background: '#1C1C1E',
    card: '#2C2C2E',
    text: '#F5F5F7',
    textSecondary: '#AEAEB2',
    border: "#FFB3B8",
    accentYellow: '#FFD700',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<Theme>(() => {
        const saved = localStorage.getItem('theme');
        if (saved === 'light' || saved === 'dark') return saved;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    useEffect(() => {
        localStorage.setItem('theme', theme);
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    const colors = theme === 'light' ? lightColors : darkColors;

    return (
        <ThemeContext.Provider value={{
            theme,
            toggleTheme,
            isDark: theme === 'dark',
            colors
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
